// @ts-nocheck
import React, { useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  SortingState,
  ExpandedState,
  useReactTable,
  Row,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Plus, ChevronDown, FolderKanban } from 'lucide-react';
import { useProjects } from '@/hooks/api/useProjects';
import { useDialogManager } from '@/hooks/useDialogManager';
import { Project } from '@/types';
import {
  ProjectTablePagination,
  TablePagination,
} from '@/components/shared/TablePagination';
import { ProjectDetailCard } from './ProjectDetailCard';
import { useProjectTableColumns } from '../hooks/useProjectTableColumns';
import { TABLE_STYLES } from '@/lib/tableStyles';
import { TableSkeleton } from '@/components/loaders/TableSkeleton';
import { cn } from '@/lib/utils';

export const ProjectTable = () => {
  const { openDialog } = useDialogManager();
  const { projects, isLoading } = useProjects();
  const columns = useProjectTableColumns();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const table = useReactTable({
    data: projects ?? [],
    columns,
    state: { sorting, globalFilter, expanded },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const total = table.getFilteredRowModel().rows.length;
  const isFiltered = globalFilter.length > 0;

  if (isLoading) return <TableSkeleton />;

  return (
    <div className="space-y-5 p-4 md:p-6 animate-in fade-in duration-300">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <FolderKanban className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground leading-tight">
              Projects
            </h1>
            <p className="text-xs text-muted-foreground">
              {total} project{total !== 1 ? 's' : ''}
              {isFiltered && ' matching search'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
            {isFiltered && (
              <button
                onClick={() => setGlobalFilter('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            )}
          </div>
          <Button
            onClick={() => openDialog('create-project')}
            size="sm"
            className="gap-1.5 h-9 shrink-0"
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">New Project</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className={TABLE_STYLES.wrapper}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className={TABLE_STYLES.headerRow}>
                  {hg.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className={TABLE_STYLES.headerCell}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableHead>
                  ))}
                  <TableHead className="w-10" />
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      onClick={() => row.toggleExpanded()}
                      data-expanded={row.getIsExpanded()}
                      className={cn(
                        TABLE_STYLES.bodyRow,
                        'cursor-pointer',
                        row.getIsExpanded() && 'border-l-2 border-l-primary/40',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={TABLE_STYLES.cell}
                          onClick={(e) => {
                            if (['actions', 'tickets'].includes(cell.column.id))
                              e.stopPropagation();
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                      <TableCell className={cn(TABLE_STYLES.cell, 'w-10')}>
                        <ChevronDown
                          className={cn(
                            'w-3.5 h-3.5 text-muted-foreground transition-transform duration-200',
                            row.getIsExpanded() && 'rotate-180',
                          )}
                        />
                      </TableCell>
                    </TableRow>

                    {row.getIsExpanded() && (
                      <TableRow className="hover:bg-transparent border-b border-border/50">
                        <TableCell colSpan={columns.length + 1} className="p-0">
                          <ProjectDetailCard project={row.original} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={columns.length + 1}
                    className="h-44 text-center"
                  >
                    <div className="flex flex-col items-center gap-2.5">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <FolderKanban className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {isFiltered ? 'No results found' : 'No projects yet'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {isFiltered
                            ? `No projects match "${globalFilter}"`
                            : 'Create your first project to get started'}
                        </p>
                      </div>
                      {!isFiltered && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDialog('create-project')}
                          className="gap-1.5 h-8 text-xs mt-1"
                        >
                          <Plus className="w-3 h-3" /> New Project
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <TablePagination noun="project" table={table} />
    </div>
  );
};
